// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { FluentProvider } from '@fluentui/react-components';
import { isNonEmptyString } from '@microsoft/vscodeedu-api';
import React, { ReactElement } from 'react';
import { createRoot } from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { BrowserRouter } from 'react-router-dom';
import { InAppFeedbackProvider } from './components/header/in-app-feedback';
import {
    ConfigContextProvider,
    CourseContextProvider,
    GithubLinkHandler,
    ProjectContextProvider,
    ProjectTemplateContextProvider,
    Redirecting,
    StandardContextProvider,
    UserContextProvider,
    UserProgressContextProvider,
    VSCodeEduIdentityProvider,
    anonymousClient,
    preloadConfig,
    routerBasename,
} from './data';
import { CurriculumContextProvider } from './data/curriculum-context';
import { PostContextProvider } from './data/post-context';
import { ThemeContextProvider } from './data/theme-context';
import './index.css';
import SignIn from './pages/sign-in';
import Routes from './routes';
import { darkTheme, traceFormFactor } from './styles';
import { initializeAppInsights, trackError, trackVerbose } from './utilities/diagnostics';
import { createReport, timeAsync, timeSync } from './utilities/performance';
import { getSharedLinkId, handleSharedLink } from './utilities/sharedLinks';

// Load config as soon as practically possible to avoid race conditions.
const configTask = preloadConfig();

const renderContent = async (element: ReactElement) => {
    await configTask;
    const container = document.getElementById('root') as HTMLElement;
    const root = createRoot(container);
    root.render(element);
};

const initialize = async () => {
    // Initialize Application Insights telemetry.
    timeSync('init-app-insights', initializeAppInsights);

    let messages: { [index: string]: string };
    const locale = 'en';
    switch (locale) {
        case 'en':
            messages = require('./lang/en.json');
            break;
    }

    try {
        // Handle Entra authentication redirect.
        const authProvider = new VSCodeEduIdentityProvider();
        await authProvider.handleEntraRedirect();

        // Handle Github Link Redirect.
        const githubLinkHandler = new GithubLinkHandler();
        await githubLinkHandler.handleGithubLinkRedirect();

        // Handle Clever portal sign in
        await authProvider.handleCleverLogin();

        // Handle vscode.dev sign-in requests.
        if ((await authProvider.handleVSCodeSilentSignIn()) || authProvider.requiresSharedLinkSignIn()) {
            // Get a possible shared link redirect for signing, will be undefined if not a shared link.
            const shareLinkRedirect = getSharedLinkId(window.location.pathname);

            await renderContent(
                <FluentProvider theme={darkTheme}>
                    <IntlProvider messages={messages} locale="en" defaultLocale="en">
                        <ConfigContextProvider>
                            <SignIn redirectTo={shareLinkRedirect} />
                        </ConfigContextProvider>
                    </IntlProvider>
                </FluentProvider>
            );
            return;
        }
    } catch (e) {
        if (e instanceof Redirecting) {
            // Skip further initialization in case of redirect.
            return;
        } else {
            // There was an error during sign-in.
            // We will show it when rendering /redirect or /signin routes.
            trackError(e, 'Error during sign-in redirect flow.');
        }
    }

    try {
        // Handle an incoming shared link.
        const shareLinkId = getSharedLinkId(window.location.pathname);
        if (isNonEmptyString(shareLinkId)) {
            const config = await configTask;
            await handleSharedLink(shareLinkId, anonymousClient, config.vscodeVersion);
        }
    } catch (e) {
        if (e instanceof Redirecting) {
            // Skip further initialization in case of redirect.
            return;
        } else {
            trackError(e, 'Error during handling of shared link.');
        }
    }

    await renderContent(
        <IntlProvider messages={messages} locale="en" defaultLocale="en">
            <BrowserRouter basename={routerBasename}>
                <ConfigContextProvider>
                    <PostContextProvider>
                        <StandardContextProvider>
                            <UserContextProvider>
                                <ThemeContextProvider>
                                    <CourseContextProvider>
                                        <UserProgressContextProvider>
                                            <ProjectTemplateContextProvider>
                                                <ProjectContextProvider>
                                                    <CurriculumContextProvider>
                                                        <InAppFeedbackProvider>
                                                            <Routes />
                                                        </InAppFeedbackProvider>
                                                    </CurriculumContextProvider>
                                                </ProjectContextProvider>
                                            </ProjectTemplateContextProvider>
                                        </UserProgressContextProvider>
                                    </CourseContextProvider>
                                </ThemeContextProvider>
                            </UserContextProvider>
                        </StandardContextProvider>
                    </PostContextProvider>
                </ConfigContextProvider>
            </BrowserRouter>
        </IntlProvider>
    );

    traceFormFactor();
};

(async () => {
    await timeAsync('init', initialize);
    trackVerbose(createReport());
})();
